import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { OroLoader } from '../components';
import PublicHeader from '../components/PublicHeader';
import StaticFooter from '../components/StaticFooter';
import AppStoreBadge from '../components/AppStoreBadge';
import OroTrademark from '../components/OroTrademark';
import EnvironmentBanner from '../components/EnvironmentBanner';
import SectionHead from '../components/marketing/SectionHead';
import MarketingSection from '../components/marketing/MarketingSection';
import { APP_PAGE } from '../data/content/app';

/**
 * "On iPhone" — the App Store landing page at `/app` (#3126, child of #3122).
 *
 * The smallest page in the marketing set on purpose: a visitor who reaches it
 * has already decided to install something, so the page makes the last few
 * seconds informative rather than re-running the argument. Compose record
 * (A0–A3, B2, B3 pass, A2 states) is on #3126.
 *
 * ROUTE NOTE: `backend/src/index.ts` also mounts `/app` (`app.route('/app',
 * appMeta)`) — but on `api.oro.fit`, a different host. This client route is on
 * `oro.fit`. No collision: the two `/app`s never resolve on the same origin.
 *
 *  - COPY lives in `data/content/app.ts` (check-voice scans it; the gallery
 *    round-trip edits it). Do not inline strings here (#2954).
 *  - ™ CADENCE (Core Rule 24): exactly ONE mark, in the opener via
 *    OroTrademark — the page's single, most-prominent brand use. Pinned by
 *    `AppPage.test.tsx`.
 *  - MOTION (B4): DELIBERATELY NONE. A visitor here is one tap from leaving and
 *    motion only delays them. No `Reveal`, no transitions on entry. The phone
 *    strip scrolls horizontally under its own overflow on narrow screens.
 *  - SCREENSHOTS are REAL product captures (never mock UI): iPhone 17 Pro
 *    simulator, dark, default Dynamic Type, Golden Hour demo club, 2026-08-19
 *    Maestro audit run (`mobile/maestro/run-audit-ios.sh`). Reused from that
 *    capture; a refresh is tracked in #3462. Provenance also in the feature doc
 *    `docs/canonical/features/marketing-app-page.md`.
 *  - Authed visitors redirect to /home, same as the front door and /members —
 *    this is a prospect surface, logged-out-only.
 *
 * The App Store badge is Apple's image asset (linking `/ios`, the store 302),
 * so it adds no ™ to this page's single mark. TEXT "get the app" affordances
 * across the site now point at THIS page (`/app`); the badge and the FAQ stay
 * on `/ios` (#3126 link split).
 */

/** Frame renders <=200 CSS px wide (the brief's cap); a horizontal scroll strip
 *  on narrow screens, centered wrap on wider. */
const FRAME_WIDTH = 'w-[168px]';

/** Block 1 — opener. Name the surface and carry the page's one ™. */
function Opener() {
  const c = APP_PAGE.opener;
  return (
    <section className="overflow-x-clip py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-6">
        <span className="block text-xs font-semibold uppercase tracking-widest text-oro-club">
          {c.eyebrow}
        </span>
        <h1 className="mt-4 max-w-2xl text-hero font-bold leading-[1.04] tracking-tight text-text-primary">
          {c.headline}
        </h1>
        <p className="mt-5 max-w-xl text-lg text-text-primary">
          {/* brandLead is optional copy (the owner may empty it on a later
              proof). When blank, render nothing before the brand — no orphan
              leading space — so the line reads exactly "ORO move™ for iPhone." */}
          {c.brandLead && <>{c.brandLead} </>}
          <OroTrademark /> {c.brandRest}
        </p>
        <p className="mt-2 max-w-xl text-base text-text-secondary">{c.sub}</p>
      </div>
    </section>
  );
}

/** Block 2 — what it is, on a phone. Three real screenshots in CSS bezels, then
 *  the requirement chips and the availability sentence. (The optional iPad
 *  paragraph is skipped while `req.ipad` is empty — see the guard below.) */
function PhoneSection() {
  const frames = APP_PAGE.frames.items;
  const req = APP_PAGE.requirements;
  return (
    <MarketingSection divider>
      {/* Horizontal scroll strip on narrow screens (flex-nowrap + overflow-x),
          wrapping and centered from `sm`. NO motion (B4). */}
      <div className="flex flex-nowrap gap-6 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center sm:overflow-x-visible">
        {frames.map((f, i) => (
          <figure key={f.src} className={`${FRAME_WIDTH} shrink-0`}>
            {/* CSS phone bezel: rounded border on the surface, soft card shadow,
                the screenshot inset with a matching inner radius. */}
            <div className="rounded-[1.9rem] border border-oro-border-hover bg-oro-surface p-2 shadow-card">
              {/* #3126 r1 (George L1): the first frame is the page's actual
                  content (LCP), so load it eagerly at high priority; frames 1
                  and 2 stay lazy. */}
              <img
                src={f.src}
                alt={f.alt}
                width={600}
                height={1304}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchPriority={i === 0 ? 'high' : undefined}
                className="w-full h-auto rounded-[1.4rem]"
              />
            </div>
            <figcaption className="mt-3 text-center text-sm font-semibold text-text-secondary">
              {f.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-3">
        {req.chips.map((chip) => (
          <span
            key={chip.text}
            className="rounded-full border border-oro-border bg-oro-surface px-4 py-1.5 text-sm font-medium text-text-secondary"
          >
            {chip.text}
          </span>
        ))}
      </div>
      <p className="mt-6 max-w-2xl text-text-secondary">{req.availability}</p>
      {/* The iPad sentence is optional (currently empty per the owner's copy
          pass). Skip the paragraph entirely when blank — no empty <p>. */}
      {req.ipad && <p className="mt-3 max-w-2xl text-text-tertiary">{req.ipad}</p>}
    </MarketingSection>
  );
}

/** Block 3 — on anything else. The state half this page's visitors are in. */
function BrowserSection() {
  const c = APP_PAGE.browser;
  return (
    <MarketingSection divider>
      <SectionHead eyebrow={c.eyebrow} title={c.title} lede={c.lede} />
    </MarketingSection>
  );
}

/** Block 4 — close. The store badge is the one primary action (Apple's asset,
 *  linking the /ios store 302). The quiet secondary link returns to the front
 *  door for a reader who wants the argument first. */
function CloseSection() {
  const c = APP_PAGE.close;
  return (
    <MarketingSection divider spacing="cta" align="center">
      <h2 className="text-section font-bold tracking-tight text-text-primary">{c.title}</h2>
      {/* The close sub-line is optional (currently empty per the owner's copy
          pass). Skip the paragraph entirely when blank — no empty <p>. */}
      {c.sub && <p className="mx-auto mt-4 max-w-md text-text-secondary">{c.sub}</p>}
      <div className="mt-8 flex flex-col items-center gap-4">
        <AppStoreBadge />
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 min-h-11 px-2 text-sm font-semibold text-oro-club hover:text-oro-gold oro-transition"
        >
          {c.secondary}
          <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
        </Link>
      </div>
    </MarketingSection>
  );
}

function AppPageContent() {
  return (
    <div className="min-h-screen bg-oro-bg text-text-primary flex flex-col">
      <EnvironmentBanner />
      <PublicHeader />
      <main className="flex-1">
        <Opener />
        <PhoneSection />
        <BrowserSection />
        <CloseSection />
      </main>
      <StaticFooter />
    </div>
  );
}

export default function AppPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // `replace` so Back does not return to /app and loop the redirect.
      navigate('/home', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Gate the render on isAuthenticated too, not just authLoading: without it an
  // authenticated visitor paints the full logged-out page for one frame before
  // the effect redirects (same guard as MembersPage). Render the loader until
  // the redirect lands.
  if (authLoading || isAuthenticated) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <OroLoader label="Loading" size={72} />
      </div>
    );
  }

  return <AppPageContent />;
}
